// Sign-in on the platform's shared host: name your company, then sign in
// with Microsoft. A tenant reaching the app on its own domain never sees
// this — the host says who they are. On the platform's shared host nothing
// in the URL does, so we ask once and remember the answer.
//
// Password / local-account sign-in is not offered. Microsoft is the only
// method on this screen.

import { useCallback, useEffect, useState } from 'react';
import { PUBLIC_API_BASE } from '../lib/api';
import {
  getWorkspaceChoice, setWorkspaceChoice, clearWorkspaceChoice,
  type WorkspaceChoice,
} from '../lib/workspaceChoice';
import { startMicrosoftSignIn } from '../lib/auth';

const card: React.CSSProperties = {
  width: 'min(420px, 100%)',
  background: 'var(--surface)',
  borderRadius: 14,
  padding: '26px 28px',
  boxShadow: '0 18px 44px rgba(15,23,42,0.18)',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--text)',
  letterSpacing: 0.2, marginBottom: 6,
};
const input: React.CSSProperties = {
  width: '100%', padding: '11px 13px', fontSize: 14, color: 'var(--text-strong)',
  border: '1px solid var(--border-strong)', borderRadius: 9, outline: 'none', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  width: '100%', padding: '11px 16px', fontSize: 14, fontWeight: 700,
  background: 'var(--brand)', color: '#fff', border: 0, borderRadius: 9, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, color: 'var(--brand-strong)',
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
};

export default function WorkspaceSignIn({
  onSignIn,
}: {
  /** Start the Microsoft redirect for the chosen company. */
  onSignIn: (choice: WorkspaceChoice) => void;
}) {
  const [chosen, setChosen] = useState<WorkspaceChoice | null>(() => getWorkspaceChoice());
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setError(null); }, [value]);

  const find = useCallback(async () => {
    const q = value.trim();
    if (!q) { setError('Enter your company name or work email.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${PUBLIC_API_BASE}/public/workspace?q=${encodeURIComponent(q)}`);
      const data = res.ok ? await res.json() : null;
      if (data?.found && data.workspace) {
        setWorkspaceChoice(data.workspace);
        setChosen(data.workspace);
      } else {
        // Deliberately the same message whether the company does not exist or
        // is not active — the sign-in screen must not confirm who is a customer.
        setError("We couldn't find that company. Check the spelling, or use your work email address.");
      }
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }, [value]);

  const useDifferent = useCallback(() => {
    clearWorkspaceChoice();
    setChosen(null);
    setValue('');
  }, []);

  // Known company — one button, straight to Microsoft.
  if (chosen) {
    return (
      <div style={card}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
          Signing in to
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 20px' }}>
          {chosen.logo_url && (
            <img src={chosen.logo_url} alt="" style={{ height: 30, width: 'auto', borderRadius: 5 }} />
          )}
          <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-strong)' }}>
            {chosen.app_name || chosen.name}
          </div>
        </div>

        <button type="button" style={primaryBtn} onClick={() => onSignIn(chosen)}>
          Continue with Microsoft
        </button>

        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <button type="button" style={linkBtn} onClick={useDifferent}>
            Sign in to a different company
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={card}>
      <form onSubmit={(e) => { e.preventDefault(); void find(); }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 800, color: 'var(--text-strong)' }}>
          Sign in
        </h1>
        <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--text)', lineHeight: 1.55 }}>
          Enter your company to continue. You'll sign in with your Microsoft work account.
        </p>

        <label htmlFor="workspace-q" style={label}>Company or work email</label>
        <input
          id="workspace-q"
          style={input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Acme Staffing, or you@acme.com"
          autoFocus
          autoComplete="organization"
          spellCheck={false}
          disabled={busy}
        />

        {error && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--danger-fg)', lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button type="submit" style={{ ...primaryBtn, marginTop: 16, opacity: busy ? 0.7 : 1 }} disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </button>

        <p style={{ margin: '14px 0 0', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Your company decides which sign-in screen you see. If you're not sure what to
          enter, use your work email address.
        </p>
      </form>

      {/* Straight to Microsoft, no company first. The lookup above picks
          branding and a login hint; this button is the one that says
          Microsoft on the shared host. */}
      <button
        type="button"
        onClick={() => startMicrosoftSignIn()}
        className="ui-btn ui-btn--secondary"
        style={{ width: '100%', marginTop: 'var(--sp-4)' }}
      >
        Sign in with Microsoft
      </button>
    </div>
  );
}
